import { Wall } from './wall';
import { Point, Utils, Fn } from './utils';
import { Mesh, Matrix4, Vector3, BufferGeometry, BufferAttribute, Vector2, MeshStandardMaterial } from 'three';
import { CoveringDTO } from 'src/app/models/product-models/DTOs/CoveringDTO';
import { TextureDataDTO } from 'src/app/models/product-models/DTOs/TextureDataDTO';
import { CoveringType } from 'src/app/models/interfaces/GenericInterfaces';

export class HalfEdge {
  /** The successor edge in CCW ??? direction. */
  public next: HalfEdge | undefined = undefined;

  /** The predecessor edge in CCW ??? direction. */
  public prev: HalfEdge | undefined = undefined;

  /** */
  public offset: number;

  /** */
  public height: number;

  /** used for intersection testing... not convinced this belongs here */
  public plane: Mesh | null = null;

  /** transform from world coords to wall planes (z=0) */
  public interiorTransform = new Matrix4();

  /** transform from world coords to wall planes (z=0) */
  public invInteriorTransform = new Matrix4();

  /** transform from world coords to wall planes (z=0) */
  public exteriorTransform = new Matrix4();

  /** transform from world coords to wall planes (z=0) */
  public invExteriorTransform = new Matrix4();

  /** */
  public redrawCallbacks: Fn[] = [];

  public id: string;

  /**
   * Constructs a half edge.
   * @param wall The corresponding wall.
   * @param front True if front side.
   */
  constructor(
    public wall: Wall,
    public front: boolean,
  ) {
    this.front = front || false;

    this.id = Utils.guid();
    this.offset = wall.getThickness() / 2.0;
    this.height = wall.getHeight();

    if (this.front) {
      this.wall.frontEdge = this;
    } else {
      this.wall.backEdge = this;
    }
  }

  /**
   *
   */
  public getTexture() {
    if (this.front) {
      return this.wall.frontTexture;
    } else {
      return this.wall.backTexture;
    }
  }

  /**
   *
   */
  public setTexture(textureUrl: string) {
    const texture = new CoveringDTO(CoveringType.Texture, new TextureDataDTO(Utils.guid(), textureUrl));

    if (this.front) {
      this.wall.frontTexture = texture;
    } else {
      this.wall.backTexture = texture;
    }

    //this.redrawCallbacks.fire();
    this.redrawCallbacks.forEach((callback: Fn) => callback());
  }

  public generatePlane(moveVector: Vector2, material: MeshStandardMaterial) {
    function transformCorner(corner: Point) {
      return new Vector3((corner.x - moveVector.x) / 100, 0, (corner.y - moveVector.y) / 100);
    }

    const v1 = transformCorner(this.interiorStart());
    const v2 = transformCorner(this.interiorEnd());
    const v3 = v2.clone();
    v3.y = this.wall.getHeight() / 100;
    const v4 = v1.clone();
    v4.y = this.wall.getHeight() / 100;

    const geometry = new BufferGeometry();
    //geometry.vertices = [v1, v2, v3, v4];

    const vertices = new Float32Array([v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v3.x, v3.y, v3.z, v4.x, v4.y, v4.z]);

    const indices = [0, 1, 2, 2, 3, 0];

    geometry.setIndex(indices);
    geometry.setAttribute('position', new BufferAttribute(vertices, 3));

    geometry.computeVertexNormals();
    geometry.computeBoundingBox();

    this.plane = new Mesh(geometry, material);

    this.computeTransforms(this.interiorTransform, this.invInteriorTransform, this.interiorStart(), this.interiorEnd());
    this.computeTransforms(this.exteriorTransform, this.invExteriorTransform, this.exteriorStart(), this.exteriorEnd());

    return this.plane;
  }

  public interiorDistance(): number {
    const start = this.interiorStart();
    const end = this.interiorEnd();
    return Utils.distance(start.x, start.y, end.x, end.y);
  }

  public distance(): number {
    return Utils.distance(this.getStart().x, this.getStart().y, this.getEnd().x, this.getEnd().y);
  }

  private computeTransforms(transform: Matrix4, invTransform: Matrix4, start: Point, end: Point) {
    const v1 = start;
    const v2 = end;

    const angle = Utils.angle(1, 0, v2.x - v1.x, v2.y - v1.y);

    const tt = new Matrix4();
    tt.makeTranslation(-v1.x, 0, -v1.y);
    const tr = new Matrix4();
    tr.makeRotationY(-angle);
    transform.multiplyMatrices(tr, tt);
    invTransform = transform.invert();
  }

  /** Gets the distance from specified point.
   * @param x X coordinate of the point.
   * @param y Y coordinate of the point.
   * @returns The distance.
   */
  public distanceTo(x: number, y: number): number {
    // x, y, x1, y1, x2, y2
    return Utils.pointDistanceFromLine(
      x,
      y,
      this.interiorStart().x,
      this.interiorStart().y,
      this.interiorEnd().x,
      this.interiorEnd().y,
    );
  }

  private getStart() {
    if (this.front) {
      return this.wall.getStart();
    } else {
      return this.wall.getEnd();
    }
  }

  private getEnd() {
    if (this.front) {
      return this.wall.getEnd();
    } else {
      return this.wall.getStart();
    }
  }

  private getOppositeEdge(): HalfEdge | null {
    if (this.front) {
      return this.wall.backEdge;
    } else {
      return this.wall.frontEdge;
    }
  }

  // these return an object with attributes x, y
  public interiorEnd(): Point {
    if (!this.next)
      return {
        x: this.getEnd().x,
        y: this.getEnd().y,
      };

    const vec = this.halfAngleVector(this, this.next);
    return {
      x: this.getEnd().x + vec.x,
      y: this.getEnd().y + vec.y,
    };
  }

  public interiorStart(): Point {
    if (!this.prev)
      return {
        x: this.getStart().x,
        y: this.getStart().y,
      };

    const vec = this.halfAngleVector(this.prev, this);
    return {
      x: this.getStart().x + vec.x,
      y: this.getStart().y + vec.y,
    };
  }

  public interiorCenter(): Point {
    return {
      x: (this.interiorStart().x + this.interiorEnd().x) / 2.0,
      y: (this.interiorStart().y + this.interiorEnd().y) / 2.0,
    };
  }

  public exteriorEnd(): Point {
    if (!this.next)
      return {
        x: this.getEnd().x,
        y: this.getEnd().y,
      };

    const vec = this.halfAngleVector(this, this.next);

    return {
      x: this.getEnd().x - vec.x,
      y: this.getEnd().y - vec.y,
    };
  }

  public exteriorStart(): Point {
    if (!this.prev)
      return {
        x: this.getStart().x,
        y: this.getStart().y,
      };

    const vec = this.halfAngleVector(this.prev, this);

    return {
      x: this.getStart().x - vec.x,
      y: this.getStart().y - vec.y,
    };
  }

  /** Get the corners of the half edge.
   * @returns An array of x,y pairs.
   */
  public corners(): Point[] {
    return [this.interiorStart(), this.interiorEnd(), this.exteriorEnd(), this.exteriorStart()];
  }

  /**
   * Gets CCW angle from v1 to v2
   */
  public halfAngleVector(v1: HalfEdge, v2: HalfEdge): Point {
    // make the best of things if we dont have prev or next
    let v1startX, v1startY, v1endX, v1endY;

    if (!v1) {
      v1startX = v2.getStart().x - (v2.getEnd().x - v2.getStart().x);
      v1startY = v2.getStart().y - (v2.getEnd().y - v2.getStart().y);
      v1endX = v2.getStart().x;
      v1endY = v2.getStart().y;
    } else {
      v1startX = <number>v1.getStart().x;
      v1startY = <number>v1.getStart().y;
      v1endX = v1.getEnd().x;
      v1endY = v1.getEnd().y;
    }

    let v2startX, v2startY, v2endX, v2endY;

    if (!v2) {
      v2startX = v1.getEnd().x;
      v2startY = v1.getEnd().y;
      v2endX = v1.getEnd().x + (v1.getEnd().x - v1.getStart().x);
      v2endY = v1.getEnd().y + (v1.getEnd().y - v1.getStart().y);
    } else {
      v2startX = v2.getStart().x;
      v2startY = v2.getStart().y;
      v2endX = v2.getEnd().x;
      v2endY = v2.getEnd().y;
    }

    // CCW angle between edges
    const theta = Utils.angle2pi(v1startX - v1endX, v1startY - v1endY, v2endX - v1endX, v2endY - v1endY);

    // cosine and sine of half angle
    const cs = Math.cos(theta / 2.0);
    const sn = Math.sin(theta / 2.0);

    // rotate v2
    const v2dx = v2endX - v2startX;
    const v2dy = v2endY - v2startY;

    const vx = v2dx * cs - v2dy * sn;
    const vy = v2dx * sn + v2dy * cs;

    // normalize
    const mag = Utils.distance(0, 0, vx, vy);
    const desiredMag = this.offset / sn;
    const scalar = desiredMag / mag;

    return {
      x: vx * scalar,
      y: vy * scalar,
    };
  }
}
